import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu as MenuIcon, ShoppingCart } from "lucide-react";
import { useTranslation } from "@/contexts/TranslationContext";

export default function Header() {
  const [shrink, setShrink] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      // Debouncing
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        // Küszöbérték
        if (scrollY > 60) {
          setShrink(true);
        } else if (scrollY < 40) {
          setShrink(false);
        }
        // Ha 40 és 60 között van, akkor nem csinálunk semmit.
      }, 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full mx-auto transition-all duration-500 ${shrink ? "h-16 sm:h-16" : "h-16 sm:h-16"
        }`}
    >
      <div
        className={`bg-black/100 ${shrink ? "sm:bg-black/80" : "sm:bg-black/100"
          } shadow-md mx-auto flex justify-between items-center pl-0 pr-3 py-2 sm:py-3 transition-all duration-500 ${shrink ? "h-16 sm:h-16" : "h-16 sm:h-16"
          }`}
      >
        {/* BAL OLDALT LEGYEN */}
        <Link href="/">
          <div className="flex items-center cursor-pointer hover:text-gray-900 transition relative">
            <div
              className={`flex items-center space-x-2 transition-all duration-500 ${shrink ? "sm:scale-75" : "scale-100"}`}
            >
              <div
                className={`relative w-16 h-16 sm:w-20 sm:h-20 transition-transform duration-500 ${shrink ? "scale-50 sm:scale-50" : "scale-75 sm:scale-75"}`}
              >
                <Image
                  src="/themes/kulturklub/logo/kulturklub_logo_2025_2.webp"
                  alt="KulturKlub.Eu weboldal logója"
                  title="KulturKlub.Eu weboldal logója"
                  fill
                  className="object-contain"
                  sizes="(max-width: 768px) 100px, 200px"
                  priority
                />
              </div>
            </div>
          </div>
        </Link>

        {/* KÖZÉPEN LEGYEN A VÉKONY KÉP */}
        <Link href="/">
          <div
            className={`flex justify-center items-center transition-transform duration-500 ${shrink ? "scale-100 sm:scale-90" : "scale-90 sm:scale-75"}`}
            style={{ height: "90%" }} // Biztosítja, hogy a szülő konténer teljes magasságát használja
          >
            <Image
              src="/themes/kulturklub/logo/kulturklub_logo_2025_szoveg.webp"
              alt="Középső vékony kép"
              width={300}
              height={50}
              className="object-contain"
            />
          </div>
        </Link>

        {/* JOBB OLDALT LEGYEN */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center h-[40px] sm:h-[52px]">

          </div>

          <div className="flex items-center justify-center h-[40px] sm:h-[52px]">
            <CartButton />
          </div>

          <div>
            <HeaderMenu />
          </div>
        </div>
      </div>
    </header>
  );
}

function CartButton() {
  return (
    <Link
      href="/cart"
      aria-label="Kosár"
      className="rounded-xl h-[44px] w-[44px] flex items-center justify-center 
      bg-zinc-900 text-white border border-zinc-700 transition hover:bg-white hover:text-black"
    >
      <ShoppingCart className="w-5 h-5" />
    </Link>
  );
}

function LanguageSelector() {
  const { locale, setLocale } = useTranslation();

  return (
    <select
      value={locale}
      onChange={(e) => setLocale(e.target.value)}
      className="bg-zinc-900 text-white border border-zinc-700 
      rounded-lg h-[44px] px-2 text-sm transition"
    >
      <option value="hu">HU</option>
    </select>
  );
}

function HeaderMenu() {
  const [open, setOpen] = useState(false);

  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // CLICK OUTSIDE + BODY SCROLL LOCK
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        open &&
        !menuRef.current?.contains(e.target) &&
        !buttonRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [open]);

  return (
    <div className="relative">
      {/* MENU BUTTON */}
      <button
        ref={buttonRef}
        aria-label="Menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="rounded-xl h-[44px] w-[44px] flex items-center justify-center 
        bg-zinc-900 text-white border border-zinc-700 transition cursor-pointer"
      >
        <MenuIcon className="w-5 h-5 text-white" />
      </button>

      {/* MENU */}
      {open && (
        <div
          ref={menuRef}
          className="absolute top-full right-0 mt-3 w-72 bg-zinc-900 border border-zinc-700
          rounded-xl shadow-xl overflow-hidden"
        >
          <MenuItem href="/" text="Főoldal" close={() => setOpen(false)} />
          <MenuItem href="/vendors/kulturklub" text="Rólunk" close={() => setOpen(false)} />
          <MenuItem href="/login" text="Fiók" close={() => setOpen(false)} />
          <MenuItem href="/cart" text="Kosár" close={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

function MenuItem({ href, text, close }) {
  return (
    <Link href={href} onClick={close} className="block">
      <div className="px-6 py-4 border-t border-zinc-700 text-white text-base font-medium 
      hover:bg-white hover:text-black transition-colors">
        {text}
      </div>
    </Link>
  );
}